/* eslint-disable no-nested-ternary,camelcase */
import BasePresenter from '@src/js/separate-logic/base-presenter';
import { dispatchProps } from '@src/js/separate-logic/decorate/dispatchProps';
import { reaction } from 'mobx';
import _get from 'lodash/get';
import { getSlideIndex, getIframeSrc, key2md5, isInMiniAppRenderWhitelist } from './utils';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import PicUrlCacheManager, { URL_REQUEST_ABORT } from './PicUrlCacheManager';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { AddonManagerEventsKeys } from '@src/slide/application/service/addon/addon-manager.interface';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

export default class RenderIframePresenter extends BasePresenter {
    private playSwapTimer: any;

    public componentDidMount() {
        const { pointerEvents } = this.props;
        const { miniAppRenderWhitelist } = this.model;
        if (!pointerEvents) {
            document.addEventListener('mousedown', this.handleMouseDown, true);
            window.addEventListener('changeIframeEvent', this.changeIframeEvent);
        }
        // 若在小程序环境中且还未获取白名单需要加载取白名单
        if (browser.isMiniProgram && !miniAppRenderWhitelist) {
            this.handleMiniRenderPrepare();
            const picUrlCacheManager = PicUrlCacheManager.getInstance();
            picUrlCacheManager.getMiniAppIframeWhiteList()
                .then((res) => {
                    this.setModel({
                        miniAppRenderWhitelist: res,
                    });
                })
                .catch((e) => {
                    window.log?.error?.(`load addon whitelist error, msg: ${e}`);
                })
                .finally(() => {
                    this.renderIframePrepare();
                });
        } else {
            this.renderIframePrepare();
        }
    }

    public componentWillUnmount() {
        if (!this.props.pointerEvents) {
            document.removeEventListener('mousedown', this.handleMouseDown, true);
            window.removeEventListener('changeIframeEvent', this.changeIframeEvent);
        }
        if (this.playSwapTimer) {
            clearTimeout(this.playSwapTimer);
        }
        this.addonManager?.event.off(AddonManagerEventsKeys.ADDON_HANDLER_IMPORT_READY, this.addonImportReadyCallback);
        if (browser.isMobile) {
            this.addonManager?.event.off(AddonManagerEventsKeys.ADDON_INFO_LOAD_READY, this.judgeIsVideoType);
        }
    }

    // eslint-disable-next-line camelcase
    public componentWillReceiveProps(nextProps) {
        if (_get(nextProps, 'addon.embedUrl') !== _get(this.props, 'addon.embedUrl')) {
            // url更改时进行封面的刷新
            this.setModel({
                loadCover: true,
                iframeSrc: getIframeSrc(nextProps),
            }, () => {
                if (!this.props.isPlayState) {
                    browser.isMiniProgram ? this.handleMiniRenderPrepare() : this.handleSnapshotLoad();
                }
            });
        }
        // 移动端浏览态依据传来的cachedElement props来切换状态
        if (nextProps.toScaleView !== this.props.toScaleView) {
            this.setModel({
                inCurrentSlide: false,
                loadCover: false,
            });
        }
    }

    @dispatchProps()
    public iframeLoadingEnd = () => {
        this.setModel({
            loadCover: false,
        });
    };

    @dispatchProps()
    public handleTouchStartNotEditMode = (e) => {
        const { miniAppRenderWhitelist } = this.model;
        const { addon } = this.props;
        if (this.props.mobileEditMode?.()
            || this.model.inCurrentSlide
            || (browser.isMiniProgram && !isInMiniAppRenderWhitelist(addon.embedUrl, miniAppRenderWhitelist))) return;
        // 点击到这，先检查上一次是否还存在激活的插件
        this.addonManager?.updateShapeModel();
        this.setModel({
            inCurrentSlide: true,
            loadCover: true,
        });
        // 记录一下这一次激活的model
        this.addonManager?.savePrevShapeModel(this.props.component, this.handleViewChange);
        e.preventDefault();
    };

    private handleViewChange = () => {
        this.setModel({
            inCurrentSlide: false,
            loadCover: false,
        });
    };

    private get component() {
        return this.props.component;
    }
    private get iframeWrapperRef() {
        return this.getChildRef('iframeWrapperRef');
    }
    private get addonManager() {
        return this.getAppServices().viewableServiceLayer.addonManager;
    }

    private renderIframePrepare = () => {
        const { isPlayState } = this.props;
        if (isPlayState) {
            this.playStateSwapSlide();
            if (this.isMobileOrNotInWhitelist()) {
                this.handleSnapshotLoad();
            }
        } else {
            this.registerNormalSwapReleaseMemory();
            this.handleSnapshotLoad();
            this.registerSDKInfoLoaded();
        }
    };

    /**
     * 注册addon-sdk信息加载完成回调
     */
    private registerSDKInfoLoaded = () => {
        this.addonManager?.event.on(AddonManagerEventsKeys.ADDON_HANDLER_IMPORT_READY, this.addonImportReadyCallback);
        // 移动端banner文案需要判断是否为视频
        if (browser.isMobile) {
            this.addonManager?.event.on(AddonManagerEventsKeys.ADDON_INFO_LOAD_READY, this.judgeIsVideoType);
        }
    };

    private addonImportReadyCallback = () => {
        const { snapshotUrl, addonName, addonIcon } = this.model;
        if (!snapshotUrl) {
            this.handleSnapshotLoad();
        }
        if (browser.isMiniProgram) {
            if (!addonName) {
                this.handleAddonNameLoad();
            }
            if (!addonIcon) {
                this.handleAddonIconLoad();
            }
        }
    };

    private playStateSwapSlide = () => {
        const { miniAppRenderWhitelist } = this.model;
        const { addon } = this.props;
        // 小程序非白名单不加载 iframe
        if (browser.isMiniProgram && !isInMiniAppRenderWhitelist(addon.embedUrl, miniAppRenderWhitelist)) return;
        this.playSwapTimer = setTimeout(() => {
            // 切页太快时，会出现多页同时加载iframe但是释放不及时的情况 这里优化一下，停留1s才给加载
            const reduxState = this.getStore().getState();
            if (this.props.slideIndex !== reduxState.control.currentPage || !this.iframeWrapperRef) return;
            this.setModel({
                inCurrentSlide: true,
                loadCover: true,
            });
        }, 1000);
    };

    private isMobileOrNotInWhitelist() {
        const { miniAppRenderWhitelist } = this.model;
        const { addon } = this.props;
        if (browser.isMobile
            || (browser.isMiniProgram && !isInMiniAppRenderWhitelist(addon.embedUrl, miniAppRenderWhitelist))) {
            return true;
        }
        return false;
    }

    /**
     * 注册pc端reaction 当页面切换时插件内存进行优化
     */
    private registerNormalSwapReleaseMemory() {
        if (this.isMobileOrNotInWhitelist()) return;
        this.disposeOnUnmount(reaction(
            () => this.getSelectionService()?.currentSlideIndex,
            (index) => {
                this.onSlideIndexChange(index);
            }, { fireImmediately: true },
        ));
        this.disposeOnUnmount(reaction(
            () => this.getSelectionService()?.slides,
            () => {
                if (this.getAppServices().viewableServiceLayer.slideRenderStateManager
                    .getStatePoints()[IStatePointEnum.allSlideConvert].isCompleted) {
                    const index = this.getSelectionService()?.currentSlideIndex;
                    this.onSlideIndexChange(index);
                }
            },
        ));
    }

    /**
     * 当pc端页面切换时，插件状态切换
     * @param index currentSlideIndex
     */
    private onSlideIndexChange = (index) => {
        const slideIndex = getSlideIndex(this.component);
        if (index === slideIndex) {
            // 非当前页进入当前页
            this.setModel({
                inCurrentSlide: true,
                loadCover: true,
            });
        } else if (index !== slideIndex && this.model.inCurrentSlide === true) {
            // 当前页进入非当前页
            this.setModel({
                inCurrentSlide: false,
            });
        }
    };

    private handleMouseDown = () => {
        if (browser.isMobile || this.props.pointerEvents) return '';
        if (this.iframeWrapperRef) {
            this.iframeWrapperRef.style.pointerEvents = 'none';
            // eslint-disable-next-line react/no-direct-mutation-state
            this.model.needEvent = false;
        }
    };

    private changeIframeEvent = () => {
        if (this.props.pointerEvents) {
            return '';
        }
        if (this.iframeWrapperRef && this.props.isSelected) {
            this.iframeWrapperRef.style.pointerEvents = 'auto';
            // eslint-disable-next-line react/no-direct-mutation-state
            this.model.needEvent = true;
        }
    };
    /**
     * 这里处理插件截图加载的逻辑
     */
    private handleSnapshotLoad = () => {
        const { originUrl, embedUrl, iframeId, extraData } = this.props.addon;
        const md5Key = key2md5({ originUrl, embedUrl });
        const picUrlCacheManager = PicUrlCacheManager.getInstance();
        const addonInfo = { originUrl, embedUrl, iframeId, extraData };
        const { width, height } = this.props.component.props.style;
        picUrlCacheManager.getScreenshotImage(addonInfo, md5Key, width, height)
            .then((res) => {
                if (typeof res === 'string') {
                    if (this.isMobileOrNotInWhitelist()) {
                        this.setModel({
                            snapshotUrl: res,
                            loadCover: false,
                        });
                    } else {
                        this.setModel({
                            snapshotUrl: res,
                        });
                    }
                } else if (res?.data?.msg !== URL_REQUEST_ABORT) {
                    this.handleLoadError(res?.data?.msg);
                }
            })
            .catch((e) => {
                this.handleLoadError(e);
            });
    };

    /**
     * 这里处理插件名的加载
     */
    private handleAddonNameLoad = () => {
        const { iframeId, extraData } = this.props.addon;
        const picUrlCacheManager = PicUrlCacheManager.getInstance();

        picUrlCacheManager.getAddonName(iframeId, extraData)
            .then((res) => {
                if (typeof res === 'string') {
                    this.setModel({
                        addonName: res,
                    });
                }
            })
            .catch((e) => {
                this.setModel({
                    loadCover: false,
                });
                window.log?.error?.(`load addon name error, msg: ${e}`);
            });
    };

    /**
     * 这里处理插件图标加载的逻辑
     */
    private handleAddonIconLoad = () => {
        const { iframeId, originUrl } = this.props.addon;
        const picUrlCacheManager = PicUrlCacheManager.getInstance();
        picUrlCacheManager.getIconOfAddonLink(iframeId, originUrl)
            .then((res) => {
                if (typeof res === 'string') {
                    this.setModel({
                        addonIcon: res,
                    });
                }
            })
            .catch((e) => {
                this.setModel({
                    loadCover: false,
                });
                window.log?.error?.(`load addon icon error, msg: ${e}`);
            });
    };

    /**
     * 根据sdk返回的map判断是否为视频
     */
    private judgeIsVideoType = () => {
        const { addon } = this.props;
        PicUrlCacheManager.getInstance().isVideoType(addon, this.addonManager)
            .then((res) => {
                this.setModel({
                    isVideoType: res,
                });
            });
    };

    /**
     * 封装cgi请求失败后window.log
     * @param msg 错误msg
     */
    private handleLoadError = (msg: string) => {
        this.setModel({
            loadCover: false,
        });
        window.log?.error?.(`load addon snapshot error, msg: ${msg}`);
    };

    /**
     * 准备插件小程序渲染所需数据: 小程序渲染白名单、iframe 截图、插件名、插件 icon
     */
    private handleMiniRenderPrepare = () => {
        this.handleSnapshotLoad();
        this.handleAddonNameLoad();
        this.handleAddonIconLoad();
    };

    private getAppServices(): ISlideBaseAppServices {
        return AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
    }

    private getSelectionService(): ISelectionService | undefined {
        const appService = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appService?.viewableServiceLayer?.selectionService;
    }
}
